import React from 'react';
import PageHero from '../components/ui/PageHero';
import FadeUp from '../components/ui/FadeUp';
import SectionHeader from '../components/ui/SectionHeader';
import ContactForm from '../components/ui/ContactForm';
import { BUSINESS } from '../data/siteData';

function ContactInfo() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      {/* Address */}
      <ContactItem icon="📍" label="Address">
        <p style={pStyle}>{BUSINESS.address.street}</p>
        <p style={pStyle}>{BUSINESS.address.city}, {BUSINESS.address.province} {BUSINESS.address.postal}</p>
      </ContactItem>

      {/* Phone */}
      <ContactItem icon="📞" label="Phone">
        <a href={`tel:${BUSINESS.phoneRaw}`} style={{ ...pStyle, color: 'var(--navy)', fontWeight: 600, textDecoration: 'none' }}
          onMouseEnter={e => e.target.style.color = 'var(--orange)'}
          onMouseLeave={e => e.target.style.color = 'var(--navy)'}
        >{BUSINESS.phone}</a>
      </ContactItem>

      {/* Email */}
      <ContactItem icon="✉️" label="Email">
        <a href={`mailto:${BUSINESS.email}`} style={{ ...pStyle, color: 'var(--navy)', fontWeight: 500, textDecoration: 'none' }}
          onMouseEnter={e => e.target.style.color = 'var(--orange)'}
          onMouseLeave={e => e.target.style.color = 'var(--navy)'}
        >{BUSINESS.email}</a>
      </ContactItem>

      {/* Hours */}
      <ContactItem icon="🕐" label="Business Hours">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.2rem 1rem' }}>
          {BUSINESS.hours.map(h => (
            <div key={h.day} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.88rem', color: 'var(--text-mid)' }}>{h.day}</span>
              <span style={{ fontSize: '0.88rem', fontWeight: 600, color: h.closed ? 'var(--text-light)' : 'var(--navy)' }}>
                {h.closed ? 'Closed' : `${h.open.replace(':00', '')} – ${h.close.replace(':00', '')}`}
              </span>
            </div>
          ))}
        </div>
      </ContactItem>

      {/* Map */}
      <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--silver-light)', height: 220 }}>
        <iframe
          src={BUSINESS.mapEmbed}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          title="Brantford Auto Wrecking location"
          style={{ width: '100%', height: '100%', display: 'block', border: 'none' }}
        />
      </div>
    </div>
  );
}

const pStyle = { fontSize: '1rem', color: 'var(--navy)', lineHeight: 1.5, fontWeight: 500 };

function ContactItem({ icon, label, children }) {
  return (
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
      <div style={{ width: 48, height: 48, flexShrink: 0, background: 'var(--navy)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem' }}>
        {icon}
      </div>
      <div>
        <h4 style={{ fontFamily: '"Barlow Condensed"', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-light)', marginBottom: '0.3rem' }}>
          {label}
        </h4>
        {children}
      </div>
    </div>
  );
}

// Can be used standalone (page) OR embedded in homepage
export default function ContactPage({ embedded = false }) {
  const inner = (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      {!embedded && (
        <FadeUp style={{ marginBottom: '3rem' }}>
          <SectionHeader tag="Get In Touch" title="Visit or Contact Us" />
        </FadeUp>
      )}
      {embedded && (
        <FadeUp style={{ marginBottom: '3rem' }}>
          <SectionHeader tag="Get In Touch" title="Visit or Contact Us" />
        </FadeUp>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem' }} className="contact-grid">
        <FadeUp>
          <ContactInfo />
        </FadeUp>
        <FadeUp delay={120}>
          <h3 style={{ fontFamily: '"Bebas Neue"', fontSize: '1.8rem', color: 'var(--navy)', letterSpacing: '0.03em', marginBottom: '1.5rem' }}>
            Request a Part or Send an Inquiry
          </h3>
          <ContactForm />
        </FadeUp>
      </div>
      <style>{`@media (max-width: 860px) { .contact-grid { grid-template-columns: 1fr !important; gap: 3rem !important; } }`}</style>
    </div>
  );

  if (embedded) {
    return (
      <section id="contact" style={{ padding: '5.5rem 2rem', background: 'white' }}>
        {inner}
      </section>
    );
  }

  return (
    <>
      <PageHero
        tag="Contact Us"
        title="Get In Touch"
        subtitle={`We're here to help. Call, email, or stop by our yard in Paris, ON.`}
      />
      <section style={{ padding: '5rem 2rem', background: 'white' }}>
        {inner}
      </section>

      {/* Extra info strip */}
      <section style={{ background: 'var(--navy)', padding: '3rem 2rem' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '2rem' }}>
          {[
            { icon: '🚗', title: 'Bring Your Vehicle', desc: 'Driving in? Bring your car info (year/make/model) and the part name to speed things up.' },
            { icon: '📋', title: 'Call Ahead', desc: 'Phone ahead to confirm availability and we\'ll have your part pulled and waiting.' },
            { icon: '💳', title: 'Payment', desc: 'We accept cash, debit, and major credit cards. Parts sold as-is unless otherwise noted.' },
            { icon: '♻️', title: 'Scrap Drop-Off', desc: 'Have a scrap vehicle? Call to arrange free pickup or drop it off during business hours.' },
          ].map(tip => (
            <div key={tip.title} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <div style={{ fontSize: '1.75rem', flexShrink: 0, marginTop: '0.1rem' }}>{tip.icon}</div>
              <div>
                <h4 style={{ fontFamily: '"Barlow Condensed"', fontWeight: 700, fontSize: '1rem', color: 'white', marginBottom: '0.3rem' }}>{tip.title}</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--silver)', lineHeight: 1.6 }}>{tip.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
